/**
 * @file service-request-modal.tsx
 * @module app/modules/service-request/components/service-request-modal
 * @description Large modal opened from the service request icon in the header. Users raise requests that need approval
 * and follow them in the Pending and Historical tabs. Users whose role may approve also get an
 * Approvals view to approve or decline requests raised by others.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import { KeyboardEvent, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { ArrowLeft, Plus } from 'lucide-react';
import { ReactComponent as ServiceBellIcon } from '../../../../_admin/assets/media/dashboard/service-bell.svg';
import { useWarehouse } from '../../../context/WarehouseContext';
import { useAuth } from '../../auth';
import { CloseModalButton } from '../../../components';
import { Popup, Toast } from '../../_common/components/pop-up';
import NewRequestForm from './new-request-form';
import RequestList from './request-list';
import { useServiceRequest } from '../hooks/use-service-request';
import {
  CreateServiceRequestPayload,
  ServiceRequest,
  ServiceRequestStatus,
  ServiceRequestScope,
  ServiceRequestTab,
} from '../types/service-request.types';
import { MAX_DECLINE_REASON_LENGTH } from '../utils/service-request.constants';
import '../styles/service-request.css';

type ServiceRequestModalProps = {
  show: boolean;
  onHide: () => void;
};

const TABS: { key: ServiceRequestTab; label: string }[] = [
  { key: 'pending', label: 'Pending requests' },
  { key: 'history', label: 'Historical requests' },
];

const SCOPES: { key: ServiceRequestScope; label: string }[] = [
  { key: 'mine', label: 'My requests' },
  { key: 'approvals', label: 'Approvals' },
];

/** forward slides in from the right, back from the left; none skips the animation. */
type SlideDirection = 'forward' | 'back' | 'none';
const TAB_ORDER: Record<ServiceRequestTab, number> = { pending: 0, history: 1 };
const SCOPE_ORDER: Record<ServiceRequestScope, number> = {
  mine: 0,
  approvals: 1,
};

const errorText = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

/** Asks for a required reason in a SweetAlert textarea; resolves to the text, or null if cancelled. */
const askReason = async (options: {
  title: string;
  message: string;
  placeholder: string;
  confirmButtonText: string;
}): Promise<string | null> => {
  const confirmation = await Popup.confirm({
    title: options.title,
    message: options.message,
    input: 'textarea',
    inputLabel: 'Reason',
    inputPlaceholder: options.placeholder,
    inputAttributes: {
      maxlength: String(MAX_DECLINE_REASON_LENGTH),
      'aria-label': 'Reason',
    },
    inputValidator: (value: string) =>
      value?.trim() ? undefined : 'Please enter a reason',
    confirmButtonText: options.confirmButtonText,
    cancelButtonText: 'Back',
    focusCancel: false,
    customClass: {
      inputLabel: 'sr-popup__label',
      input: 'sr-popup__textarea',
    },
  });
  if (!confirmation.isConfirmed) return null;
  return typeof confirmation.value === 'string' ? confirmation.value : '';
};

const ServiceRequestModal = ({ show, onHide }: ServiceRequestModalProps) => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const {
    requestableActions,
    actionsByKey,
    canApprove,
    scope,
    setScope,
    tab,
    setTab,
    records,
    counts,
    newSince,
    loading,
    error,
    busyId,
    refresh,
    createRequest,
    approveRequest,
    declineRequest,
    withdrawRequest,
  } = useServiceRequest(show, String(currentUser?._id || ''));
  const [view, setView] = useState<'list' | 'new'>('list');
  const [highlightId, setHighlightId] = useState<string | null>(null);
  // Direction the body slides in on the next change; "none" while the modal opens.
  const [slide, setSlide] = useState<SlideDirection>('none');

  useEffect(() => {
    if (!show) {
      setView('list');
      setHighlightId(null);
      setSlide('none');
    }
  }, [show]);

  const canRequest = requestableActions.length > 0;
  const pendingCount =
    scope === 'mine' ? counts.minePending : counts.approvalsPending;

  let subtitle = 'Raise requests that need approval and follow their status.';
  if (view === 'new') {
    subtitle =
      'Raise a request. It runs automatically once an approver approves it.';
  } else if (scope === 'approvals') {
    subtitle =
      'Review requests from your team. Approving runs the action straight away.';
  }

  const goToTab = (next: ServiceRequestTab) => {
    if (next === tab) return;
    setSlide(TAB_ORDER[next] > TAB_ORDER[tab] ? 'forward' : 'back');
    setTab(next);
  };

  const goToScope = (next: ServiceRequestScope) => {
    if (next === scope) return;
    setSlide(SCOPE_ORDER[next] > SCOPE_ORDER[scope] ? 'forward' : 'back');
    setScope(next);
  };

  const openNewRequest = () => {
    setSlide('forward');
    setView('new');
  };

  const backToList = () => {
    setSlide('back');
    setView('list');
  };

  const handleCreate = async (body: CreateServiceRequestPayload) => {
    const created = await createRequest(body, selectedWarehouse?.value);
    void Toast.success(`${created.refKey} submitted for approval`);
    setHighlightId(created._id);
    setScope('mine');
    setTab('pending');
    backToList();
    void refresh();
  };

  const handleApprove = async (request: ServiceRequest) => {
    const confirmation = await Popup.confirm({
      title: `Approve ${request.refKey}?`,
      message: `${request.actionLabel} runs as soon as you approve. This cannot be undone.`,
      confirmButtonText: 'Approve',
      cancelButtonText: 'Back',
    });
    if (!confirmation.isConfirmed) return;
    try {
      const updated = await approveRequest(request);
      const outcome = updated.result?.message;
      if (updated.status === ServiceRequestStatus.Failed) {
        // Approved, but the action refused or failed; the request is now Failed with the reason.
        void Toast.error(
          `${updated.refKey} failed: ${outcome || 'the action did not run'}`
        );
        return;
      }
      void Toast.success(
        outcome
          ? `${updated.refKey} approved: ${outcome}`
          : `${updated.refKey} approved`
      );
    } catch (approveError) {
      void Toast.error(
        errorText(approveError, 'Unable to approve the request.')
      );
    }
  };

  const handleDecline = async (request: ServiceRequest) => {
    const comment = await askReason({
      title: `Decline ${request.refKey}?`,
      message: `${
        request.requestedBy.name || 'The requester'
      } will see your reason.`,
      placeholder: 'Explain why, and what the requester should do instead.',
      confirmButtonText: 'Decline request',
    });
    if (comment === null) return;
    try {
      const updated = await declineRequest(request, comment);
      void Toast.success(`${updated.refKey} declined`);
    } catch (declineError) {
      void Toast.error(
        errorText(declineError, 'Unable to decline the request.')
      );
    }
  };

  const handleWithdraw = async (request: ServiceRequest) => {
    const reason = await askReason({
      title: `Withdraw ${request.refKey}?`,
      message: 'It will no longer go to an approver.',
      placeholder:
        'For example: raised by mistake, customer changed their mind.',
      confirmButtonText: 'Withdraw request',
    });
    if (reason === null) return;
    try {
      const updated = await withdrawRequest(request, reason);
      void Toast.success(`${updated.refKey} withdrawn`);
    } catch (withdrawError) {
      void Toast.error(
        errorText(withdrawError, 'Unable to withdraw the request.')
      );
    }
  };

  /** Left/right arrows move between tabs, as in a native tab strip. */
  const handleTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const next =
      TABS[
        (index + (event.key === 'ArrowRight' ? 1 : TABS.length - 1)) %
          TABS.length
      ];
    goToTab(next.key);
    document.getElementById(`sr-tab-${next.key}`)?.focus();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      enforceFocus={false}
      dialogClassName="sr-modal"
      aria-labelledby="sr-modal-title"
    >
      <Modal.Header className="sr-modal__header">
        <div className="sr-modal__heading">
          <span className="sr-modal__icon">
            <ServiceBellIcon width={22} height={22} aria-hidden="true" />
          </span>
          <div>
            <Modal.Title id="sr-modal-title" className="sr-modal__title">
              Self Service
            </Modal.Title>
            <div className="sr-modal__subtitle">{subtitle}</div>
          </div>
        </div>
        <div className="sr-modal__header-actions">
          {view === 'list' && canRequest ? (
            <button
              type="button"
              className="uw-btn uw-btn-primary btn btn-sm btn-primary"
              onClick={openNewRequest}
            >
              <Plus size={16} className="me-1" aria-hidden="true" />
              New request
            </button>
          ) : null}
          <CloseModalButton onClick={onHide} />
        </div>
      </Modal.Header>

      {view === 'list' ? (
        <div className="sr-toolbar">
          <div className="sr-tabs" role="tablist" aria-label="Request lists">
            {TABS.map((item, index) => {
              const selected = tab === item.key;
              return (
                <button
                  key={item.key}
                  id={`sr-tab-${item.key}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls="sr-tabpanel"
                  tabIndex={selected ? 0 : -1}
                  className={`sr-tab${selected ? ' is-active' : ''}`}
                  onClick={() => goToTab(item.key)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                >
                  {item.label}
                  {item.key === 'pending' && pendingCount > 0 ? (
                    <span className="sr-count">{pendingCount}</span>
                  ) : null}
                  {item.key === 'history' &&
                  scope === 'mine' &&
                  counts.newDecisions > 0 ? (
                    <span
                      className="sr-count sr-count--new"
                      title="Decided since you last looked"
                    >
                      {counts.newDecisions}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          {canApprove ? (
            <div
              className="sr-segmented"
              role="group"
              aria-label="Show requests"
            >
              {SCOPES.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  aria-pressed={scope === item.key}
                  className={`sr-segmented__item${
                    scope === item.key ? ' is-active' : ''
                  }`}
                  onClick={() => goToScope(item.key)}
                >
                  {item.label}
                  {/* Once in Approvals, the Pending tab shows this count */}
                  {item.key === 'approvals' &&
                  scope !== 'approvals' &&
                  counts.approvalsPending > 0 ? (
                    <span className="sr-count sr-count--attention">
                      {counts.approvalsPending}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <Modal.Body className="sr-modal__body">
        {/* A new key on every view change replays the slide-in animation */}
        <div
          key={view === 'new' ? 'new' : `${scope}:${tab}`}
          className={`sr-slide sr-slide--${slide}`}
        >
          {view === 'new' ? (
            <div className="sr-new-request">
              <button
                type="button"
                className="btn btn-link btn-sm p-0 mb-5 sr-link sr-back"
                onClick={backToList}
              >
                <ArrowLeft size={16} aria-hidden="true" />
                Back to requests
              </button>
              <NewRequestForm
                actions={requestableActions}
                onCancel={backToList}
                onSubmit={handleCreate}
              />
            </div>
          ) : (
            <div
              id="sr-tabpanel"
              role="tabpanel"
              aria-labelledby={`sr-tab-${tab}`}
            >
              <RequestList
                records={records}
                actionsByKey={actionsByKey}
                loading={loading}
                error={error}
                scope={scope}
                tab={tab}
                busyId={busyId}
                highlightId={highlightId}
                newSince={newSince}
                onRetry={() => void refresh()}
                onApprove={(request) => void handleApprove(request)}
                onDecline={(request) => void handleDecline(request)}
                onWithdraw={(request) => void handleWithdraw(request)}
              />
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ServiceRequestModal;
